import { useState } from 'react'
import { api } from '@/lib/api'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { useAuthStore } from '@/stores/authStore'
import { pearDemoOrganizationId } from './demoAccess'

// The superuser names the existing account at setup time; no employee is hardcoded here.
export function normalizeStaffEmail(value: string): string | null {
 const email=value.trim().toLowerCase()
 return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)?email:null
}

export default function DemoSetup() {
 const user=useAuthStore(s=>s.user)
 const [busy,setBusy]=useState(false)
 const [message,setMessage]=useState('')
 const [id,setId]=useState('')
 const [staffEmail,setStaffEmail]=useState('')
 if (!user?.is_superuser) return null
 const email=normalizeStaffEmail(staffEmail)
 async function setup() {
  if(!email){setMessage('Enter the email of an existing account to add as staff.');return}
  setBusy(true);setMessage('')
  try {
   const orgs=await api.listOrganizations()
   const matches=orgs.filter(org=>org.name==='Pear Demo')
   if (!pearDemoOrganizationId && matches.length>1) throw new Error('Multiple Pear Demo organizations exist. Configure the intended organization ID before continuing.')
   const org=pearDemoOrganizationId?await api.getOrganization(pearDemoOrganizationId):matches[0]||await api.createOrganization({name:'Pear Demo'})
   if(org.name!=='Pear Demo') throw new Error('Configured organization is not Pear Demo. Check its ID.')
   setId(org.id)
   const existing=org.users.find(member=>member.user.email.toLowerCase()===email)
   if(existing && existing.role!=='staff') throw new Error('This account already has a different organization role. Review it in organization settings; no permissions were changed.')
   if(!existing) await api.addUserToOrganization(org.id,email,'staff')
   const verified=await api.getOrganization(org.id)
   if(!verified.users.some(member=>member.user.email.toLowerCase()===email&&member.role==='staff')) throw new Error('Staff membership could not be verified.')
   setMessage(`Pear Demo exists and ${email} is a staff member. No superuser permissions were changed. Configure the ID below and rebuild to enable member access.`)
  } catch(error) {setMessage(`Setup incomplete: ${error instanceof Error?error.message:'Check organization membership and retry. The existing account must already exist; no invitation is sent.'}`)}
  finally {setBusy(false)}
 }
 return <details className="rounded-xl border bg-card p-4 text-sm"><summary className="cursor-pointer font-medium">Pear Demo setup</summary><p className="my-3 text-muted-foreground">Creates or reuses Pear Demo in the connected API and adds an existing account as staff. This action changes organization membership; it does not invite users or grant superuser access.</p><label className="mb-3 block max-w-sm font-medium">Staff account email<Input className="mt-1" type="email" autoComplete="off" value={staffEmail} onChange={e=>{setStaffEmail(e.target.value);setMessage('')}} placeholder="name@example.com"/><span className="mt-1 block text-xs font-normal text-muted-foreground">Must already have an account. No invitation is sent.</span></label><Button onClick={setup} disabled={busy||!email}>{busy?'Setting up…':'Create Pear Demo + add staff'}</Button>{message&&<p className="mt-3" role="status">{message}</p>}{id&&<p className="mt-3 break-all font-mono text-xs">REACT_APP_PEAR_DEMO_ORGANIZATION_ID={id}</p>}</details>
}
